import { createContext, useContext, useEffect, useRef, useState } from "react";

import { Modal } from "reactstrap";

/*
 * Painel do arquivo fixo ao lado da tela (ex: posse): o arquivo aberto na janela flutuante (ModalViewArquivo com flutuante)
 * pode ser fixado do lado esquerdo da tela que abriu, como parte dela, com a tela ao lado.
 * Spec: documentos/specs/2026-10-24-posse-documento-fixo.md
 *
 * Uso na tela:
 *  <PainelArquivoProvider chave="posse">              -> envolve a tela (o ModalViewArquivo dentro dela ganha o botão Fixar)
 *    <ModalPainelArquivo isOpen... size="xxl">        -> no lugar do Modal: ocupa a largura toda com o arquivo fixado
 *      <PainelArquivoCabecalho><ModalHeader>...</ModalHeader></PainelArquivoCabecalho>  -> some com o arquivo fixado
 *      <PainelArquivoLayout tituloTela="...">conteúdo da tela</PainelArquivoLayout>  -> arquivo à esquerda (com o título
 *                                                       da tela acima dele) e o conteúdo ao lado
 *
 * Regras:
 *  - Fixado, todo arquivo aberto na tela vai para o painel, no lugar do que estava nele (o anterior é fechado).
 *  - Soltar: o arquivo do painel volta para a janela flutuante. Sem o arquivo fixado, cada arquivo abre na sua janela,
 *    a não ser com janelaUnica: aí o arquivo aberto na janela flutuante substitui o que estava aberto (fecha a janela anterior).
 *  - A opção de fixar e a largura do painel ficam guardadas no navegador (localStorage), por tela (chave).
 *    Com sempreFixado, a tela abre sempre com o arquivo fixado (a opção de fixar não é guardada; a largura e o zoom sim).
 *  - Zoom da tela ao lado do painel (de 80% a 100%): começa no zoom em que a tela cabe na largura que sobra
 *    (fn_getZoomAutomaticoTela); o usuário pode mudar pelos botões do painel, e a escolha fica guardada no navegador.
 */

const PainelArquivoContext = createContext(null);

//Largura mínima do painel e do conteúdo da tela ao lado (px)
const LARGURA_MINIMA_PAINEL = 320;
const LARGURA_MINIMA_TELA = 600;

//Zoom da tela ao lado do painel
export const ZOOM_TELA_MINIMO = 0.8;
export const ZOOM_TELA_MAXIMO = 1;
const ZOOM_TELA_PASSO = 0.05;

//Largura (px, em 100%) em que a tela ao lado do painel fica confortável, sem quebrar os títulos dos campos (ex: posse)
const LARGURA_CONFORTAVEL_TELA = 1100;

//Arredonda o zoom para o passo (5%) e deixa entre o mínimo e o máximo
function getZoomValido(zoom) {
  const arredondado = Math.round(zoom * 100) / 100;

  return Math.max(ZOOM_TELA_MINIMO, Math.min(arredondado, ZOOM_TELA_MAXIMO));
}

/*
 * Retorna o zoom inicial da tela ao lado do painel: o maior zoom (de 5 em 5%) em que a tela cabe na largura que sobra
 * ao lado do painel, entre 80% e 100%. Ex: tela grande fica em 100%; notebook, menor.
 * @larguraJanela: largura da janela do navegador. @larguraPainel: largura do painel do arquivo (px)
 */
export function fn_getZoomAutomaticoTela(larguraJanela, larguraPainel) {
  const larguraDisponivel = larguraJanela - larguraPainel - 6; //6: divisória
  const zoom =
    Math.floor((larguraDisponivel / LARGURA_CONFORTAVEL_TELA) * 20) / 20;

  return getZoomValido(zoom);
}

//Lê o valor guardado no navegador (sem localStorage disponível, usa o padrão)
function lerPreferencia(chave, padrao) {
  try {
    const valor = localStorage.getItem(chave);
    return valor == null ? padrao : JSON.parse(valor);
  } catch {
    return padrao;
  }
}

//Guarda o valor no navegador (se não der, só não fica guardado)
function gravarPreferencia(chave, valor) {
  try {
    localStorage.setItem(chave, JSON.stringify(valor));
  } catch {
    //sem localStorage: a preferência vale só enquanto a tela estiver aberta
  }
}

//Largura padrão do painel: 40% da janela do navegador
function getLarguraPadrao() {
  return Math.max(LARGURA_MINIMA_PAINEL, Math.round(window.innerWidth * 0.4));
}

/*
 * Contexto do painel do arquivo da tela.
 * @chave: identificação da tela para guardar a preferência (ex: "posse")
 * @sempreFixado: a tela abre sempre com o arquivo fixado, e soltar vale só enquanto a tela estiver aberta (ex: conferência)
 * @janelaUnica: na janela flutuante, abrir um arquivo fecha a janela do arquivo anterior (uma janela só, ex: posse)
 */
export function PainelArquivoProvider({
  chave,
  sempreFixado = false,
  janelaUnica = false,
  children,
}) {
  const chaveFixado = `siig_painel_arquivo_${chave}_fixado`;
  const chaveLargura = `siig_painel_arquivo_${chave}_largura`;
  const chaveZoom = `siig_painel_arquivo_${chave}_zoom`;

  //### USE REF ###
  //Arquivo do painel (para fechar o anterior ao abrir outro, sem depender da renderização)
  const arquivoRef = useRef(null);
  //Arquivo aberto na janela flutuante (com janelaUnica, para fechar a janela anterior ao abrir outra)
  const janelaRef = useRef(null);

  //### USE STATE ###
  const [isFixado, setIsFixado] = useState(() =>
    sempreFixado ? true : lerPreferencia(chaveFixado, false),
  );
  //Arquivo no painel: { id, pastaArquivo, nomeArquivo, fechar }
  const [arquivo, setArquivo] = useState(null);
  const [largura, setLargura] = useState(() =>
    lerPreferencia(chaveLargura, getLarguraPadrao()),
  );
  //Zoom escolhido pelo usuário (null: o automático, conforme a largura que sobra ao lado do painel)
  const [zoomEscolhido, setZoomEscolhido] = useState(() =>
    lerPreferencia(chaveZoom, null),
  );
  const [larguraJanela, setLarguraJanela] = useState(() => window.innerWidth);

  //### VARIÁVEIS AUXILIARES ###
  const zoomTela =
    zoomEscolhido != null
      ? getZoomValido(zoomEscolhido)
      : fn_getZoomAutomaticoTela(larguraJanela, largura);

  //### USE EFFECT ###
  //Acompanha a largura da janela do navegador (para o zoom automático)
  useEffect(() => {
    function handleResize() {
      setLarguraJanela(window.innerWidth);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  //### FUNÇÕES ###
  //Mostra o arquivo no painel, fechando o que estava nele (a janela que abriu o anterior)
  function abrirNoPainel(item) {
    const anterior = arquivoRef.current;

    if (anterior && anterior.id != item.id) {
      anterior.fechar?.();
    }

    arquivoRef.current = item;
    setArquivo(item);
  }

  //Janela flutuante aberta: com janelaUnica, fecha a janela do arquivo anterior
  function abrirJanela(item) {
    if (!janelaUnica) return;

    const anterior = janelaRef.current;

    if (anterior && anterior.id != item.id) {
      anterior.fechar?.();
    }

    janelaRef.current = item;
  }

  //Janela flutuante fechada (ou o arquivo foi para o painel)
  function fecharJanela(id) {
    if (janelaRef.current?.id == id) {
      janelaRef.current = null;
    }
  }

  //Fixa o arquivo da janela flutuante no painel
  function fixar(item) {
    setIsFixado(true);
    if (!sempreFixado) {
      gravarPreferencia(chaveFixado, true);
    }
    abrirNoPainel(item);
  }

  //Solta o arquivo do painel: volta para a janela flutuante (a janela que abriu continua aberta)
  function soltar() {
    setIsFixado(false);
    if (!sempreFixado) {
      gravarPreferencia(chaveFixado, false);
    }
    arquivoRef.current = null;
    setArquivo(null);
  }

  //Fecha o arquivo do painel (continua fixado: o próximo arquivo aberto vem para o painel)
  function fecharPainel() {
    const atual = arquivoRef.current;

    arquivoRef.current = null;
    setArquivo(null);
    atual?.fechar?.();
  }

  //Aumenta ou diminui o zoom da tela ao lado do painel (de 5 em 5%), guardando a escolha do usuário
  function alterarZoomTela(aumentar) {
    const novoZoom = getZoomValido(
      zoomTela + (aumentar ? ZOOM_TELA_PASSO : -ZOOM_TELA_PASSO),
    );

    setZoomEscolhido(novoZoom);
    gravarPreferencia(chaveZoom, novoZoom);
  }

  //Altera a largura do painel, sem passar do mínimo do painel e do conteúdo da tela
  function alterarLargura(novaLargura, gravar = false) {
    const larguraValida = Math.max(
      LARGURA_MINIMA_PAINEL,
      Math.min(novaLargura, window.innerWidth - LARGURA_MINIMA_TELA),
    );

    setLargura(larguraValida);

    if (gravar) {
      gravarPreferencia(chaveLargura, larguraValida);
    }
  }

  return (
    <PainelArquivoContext.Provider
      value={{
        isFixado,
        arquivo,
        largura,
        abrirNoPainel,
        fixar,
        soltar,
        fecharPainel,
        alterarLargura,
        zoomTela,
        alterarZoomTela,
        abrirJanela,
        fecharJanela,
      }}
    >
      {children}
    </PainelArquivoContext.Provider>
  );
}

//Retorna o contexto do painel do arquivo (null fora de uma tela com PainelArquivoProvider)
export function usePainelArquivo() {
  return useContext(PainelArquivoContext);
}

/*
 * Modal da tela com o painel do arquivo: com um arquivo fixado, ocupa a largura e a altura toda da janela do navegador
 * (os demais atributos são os do Modal do reactstrap)
 * @classNameComArquivo: classe aplicada só com um arquivo fixado (ex: a posse fica na altura da janela só com o documento fixado)
 */
export function ModalPainelArquivo({
  className,
  classNameComArquivo,
  children,
  ...rest
}) {
  const painel = usePainelArquivo();
  const comArquivo = painel?.arquivo != null;

  return (
    <Modal
      {...rest}
      className={`${className || ""} ${comArquivo ? `modal-com-painel-arquivo ${classNameComArquivo || ""}` : ""}`}
    >
      {children}
    </Modal>
  );
}

/*
 * Barra de título da tela (ModalHeader): com o arquivo fixado não aparece, para caber mais na altura
 * (o título da tela vai para cima do arquivo, no PainelArquivoLayout, e a tela é fechada pelo botão do rodapé)
 */
export function PainelArquivoCabecalho({ children }) {
  const painel = usePainelArquivo();

  return painel?.arquivo != null ? null : children;
}

/*
 * Conteúdo da tela com o painel do arquivo: o arquivo fixado à esquerda, a divisória (arrasta para mudar a largura)
 * e o conteúdo da tela ao lado. Sem arquivo fixado, só o conteúdo, como antes.
 * @renderArquivo: função que recebe o arquivo do painel ({ pastaArquivo, nomeArquivo }) e o contexto do painel
 *                 (para o botão fechar: painel.fecharPainel) e retorna a visualização do arquivo
 * @tituloTela: título da tela, apresentado acima do arquivo (a barra de título da tela some com o arquivo fixado)
 */
export function PainelArquivoLayout({ renderArquivo, tituloTela, children }) {
  const painel = usePainelArquivo();

  //### USE REF ###
  const arrastoRef = useRef(null);

  //### USE STATE ###
  //Enquanto arrasta a divisória, o arquivo fica coberto para o PDF (iframe) não "roubar" o movimento do mouse
  const [arrastando, setArrastando] = useState(false);

  const comArquivo = painel?.arquivo != null;

  //### EVENTOS ###
  function handlePointerDownDivisoria(event) {
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    arrastoRef.current = { inicioX: event.clientX, largura: painel.largura };
    setArrastando(true);
  }

  function handlePointerMoveDivisoria(event) {
    const arrasto = arrastoRef.current;
    if (!arrasto) return;

    painel.alterarLargura(arrasto.largura + event.clientX - arrasto.inicioX);
  }

  function handlePointerUpDivisoria(event) {
    const arrasto = arrastoRef.current;
    if (!arrasto) return;

    event.currentTarget.releasePointerCapture?.(event.pointerId);
    painel.alterarLargura(
      arrasto.largura + event.clientX - arrasto.inicioX,
      true,
    );
    arrastoRef.current = null;
    setArrastando(false);
  }

  //IMPORTANTE: a estrutura é sempre a mesma, com ou sem o arquivo (só o painel e a divisória entram e saem).
  //Se mudasse, o React montaria a tela de novo ao fixar e ao soltar, e a tela perderia o que já foi digitado.
  return (
    <div
      className={
        comArquivo ? "painel-arquivo-layout" : "painel-arquivo-sem-arquivo"
      }
    >
      {comArquivo && (
        <div
          className="painel-arquivo"
          //A largura muda ao arrastar a divisória
          style={{ width: painel.largura }}
        >
          {tituloTela && (
            <div className="painel-arquivo-titulo-tela">{tituloTela}</div>
          )}

          {renderArquivo(painel.arquivo, painel)}

          {arrastando && <div className="janela-flutuante-cobertura" />}
        </div>
      )}

      {comArquivo && (
        <div
          className="painel-arquivo-divisoria"
          title="Arraste para mudar a largura"
          onPointerDown={handlePointerDownDivisoria}
          onPointerMove={handlePointerMoveDivisoria}
          onPointerUp={handlePointerUpDivisoria}
          onPointerCancel={handlePointerUpDivisoria}
        />
      )}

      <div
        className={
          comArquivo ? "painel-arquivo-tela" : "painel-arquivo-tela-sem-arquivo"
        }
        //Zoom da tela ao lado do painel (só com o arquivo fixado)
        style={comArquivo ? { zoom: painel.zoomTela } : undefined}
      >
        {children}
      </div>
    </div>
  );
}
